import { Component, ChangeDetectionStrategy, OnChanges, Input, SimpleChanges, Output, EventEmitter, ViewChild } from '@angular/core';
import { NgbDateStruct, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { ParamData } from "../../models/param-data";
import { PeriodData } from "../../models/period-data";
import { YearCheck } from "../../models/year-check";
import { FormsModule } from '@angular/forms';
import { NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap/datepicker';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: "parameter-sidenav-period",
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./parameter-sidenav.period.component.html",
    styleUrls: ['./parameter-sidenav.period.component.scss'],
    imports: [FormsModule, NgbInputDatepicker, TranslatePipe]
})
export class ParameterSideNavPeriodComponent implements OnChanges {

    @Input() minYear: number;
    @Input() maxYear: number;
    @Input() startdat: ParamData;
    @Input() stopdat: ParamData;
    @Input() useYearChecks: boolean = false;
    @Output() periodChanged = new EventEmitter();

    private lastValidDate:       NgbDateStruct;

    public fromMinDate: NgbDateStruct;
    public fromMaxDate: NgbDateStruct;
    public toMinDate:   NgbDateStruct;
    public toMaxDate:   NgbDateStruct;
    public modelFrom:   NgbDateStruct;
    public modelTo:     NgbDateStruct;
    public yearChecks:  YearCheck[] = [];
    public chosenYears = [];
    public fromYears: number[];
    public fromYear: number;
    public toYears: number[];
    public toYear: number;
    public showCurrentYear: boolean;


    @ViewChild("dpFrom", {static: false}) dpFrom: NgbDatepicker;
    @ViewChild("dpTo", {static: false}) dpTo: NgbDatepicker;

    public updatingParam = false;

    constructor() {
        this.setLastValidDate();
    }

    private setLastValidDate() {
        if (this.maxYear) {
            this.lastValidDate = { year: this.maxYear, month: 1, day: 1 };
        } else {
            this.lastValidDate = { year: moment().year(), month: moment().month() + 1, day: moment().date() };
        }
    }

    /**
     * Uppdateringar av datum-datan behöver omvandlas till objekt som passar till datepickern.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges) {
        this.loadPeriods();
        if (changes["startdat"] || changes["stopdat"]) {
            this.updatingParam = true; //Stäng av automatisk uppdatering av rapport via selectedChange under tiden komponenten uppdateras med data uppifrån.
            if (this.startdat.data && this.startdat.data.length > 0) {
                let startData = this.startdat.data.split("-");
                let fromDateStruct = { year: +startData[0], month: +startData[1], day: +startData[2] };
                this.modelFrom = fromDateStruct;
                this.toMinDate = fromDateStruct;
            }
            if (this.stopdat.data && this.stopdat.data.length > 0) {
                let stopData = this.stopdat.data.split("-");
                let toDateStruct = { year: +stopData[0], month: +stopData[1], day: +stopData[2] };
                this.modelTo = toDateStruct;
                this.fromMaxDate = toDateStruct;
            }
            this.updateYearsFromPeriod();
            setTimeout(() => {
                this.updatingParam = false; //Återställ uppdateringar uppåt.
            });
        }
    }

    private updateYearsFromPeriod() {
        if (this.modelFrom.month === 1 &&
            this.modelFrom.day === 1 &&
            this.modelTo.month === 12 &&
            this.modelTo.day === 31) {
            //Hela år användes, yearChecks går att använda
            this.yearChecks.forEach((yearCheck) => {
                yearCheck.checked = yearCheck.year >= this.modelFrom.year && yearCheck.year <= this.modelTo.year;
                yearCheck.disabled = yearCheck.year > this.modelFrom.year && yearCheck.year < this.modelTo.year;
            });
            this.fromYear = this.modelFrom.year;
            this.toYear = this.modelTo.year;
            this.updateFromYears();
            this.updateToYears();
        } else {
            //Hela år är inte angivet. Rensa years
            this.clearAllYears();
        }
        this.setShowCurrentYear();
    }

    private clearAllYears(): void {
        this.yearChecks.forEach((yearCheck) => {
            yearCheck.checked = false;
            yearCheck.disabled = false;
        });
        this.fromYear = undefined;
        this.toYear = undefined;
        this.updateFromYears();
        this.updateToYears();
    }

    /**
     * Beräknar om "Ett år från idag" skall vara ikryssat
     */
    private setShowCurrentYear(): void {
        this.showCurrentYear =
            this.modelTo.year === this.lastValidDate.year &&
            this.modelTo.month === this.lastValidDate.month &&
            this.modelTo.day === this.lastValidDate.day &&
            this.modelFrom.year === this.lastValidDate.year - 1 &&
            this.modelFrom.month === this.lastValidDate.month &&
            this.modelFrom.day === this.lastValidDate.day;
    }

    /**
     * Sätt defaultdatum till datepickern och skapa checkboxes för att välja år.
     */
    public loadPeriods() {
        let minDate = this.minYear ? moment(`${this.minYear}-01-01`).toDate() : moment("2008-01-01").toDate();
        let maxDate = this.maxYear ? moment(`${this.maxYear}-12-31`).toDate() : moment(this.lastValidDate.year + "-12-31").toDate();
        let minDateStruct = { year: minDate.getFullYear(), month: minDate.getMonth() + 1, day: minDate.getDate() };
        let maxDateStruct = { year: maxDate.getFullYear(), month: maxDate.getMonth() + 1, day: maxDate.getDate() };
        this.minYear = minDate.getFullYear();
        this.maxYear = maxDate.getFullYear();
        this.fromMinDate = minDateStruct;
        this.fromMaxDate = maxDateStruct;
        this.toMinDate = minDateStruct;
        this.toMaxDate = maxDateStruct;
        this.setCurrentYearPeriod();
        let _yearChecks = [];
        let _allYears = [];
        for (let year = this.minYear; year <= this.maxYear; year++) {
            _yearChecks.push(new YearCheck(year, false, false));
            _allYears.push(year);
        }
        this.yearChecks = _yearChecks;
        this.fromYears = _allYears;
        this.toYears = _allYears;
    }

    /**
     * Sätt ett från bakåt från idag.
     */
    private setCurrentYearPeriod(): void {
        this.modelFrom = { year: this.lastValidDate.year - 1, month: this.lastValidDate.month, day: this.lastValidDate.day };
        this.modelTo = { year: this.lastValidDate.year, month: this.lastValidDate.month, day: this.lastValidDate.day };
    }

    /**
     * En år-checkbox är klickad, gå igenom alla checkboxar och perioder
     */
    public yearWasChecked(): void {
        this.updatePeriodFromYearChecks();
        this.updateYearsFromPeriod();
        this.dpFrom.navigateTo(this.modelFrom);
        this.dpTo.navigateTo(this.modelTo);
        this.setShowCurrentYear();
        this.periodChange();
    }

    /**
     * Beräknar period utifrån iklickade år-checkboxar
     */
    private updatePeriodFromYearChecks(): void {
        let firstYear = this.yearChecks[this.yearChecks.length - 1].year;
        let lastYear = this.yearChecks[0].year;
        this.yearChecks.forEach((checkYear) => {
            if (checkYear.checked && checkYear.year < firstYear) {
                firstYear = checkYear.year;
            }
            if (checkYear.checked && checkYear.year > lastYear) {
                lastYear = checkYear.year;
            }
        });
        if (firstYear > lastYear) {
            //Inga markerade år.
            this.setCurrentYearPeriod();
            this.setShowCurrentYear();
            return;
        }
        this.modelFrom = {
            year: firstYear,
            month: 1,
            day: 1
        };
        this.modelTo = {
            year: lastYear,
            month: 12,
            day: 31
        };
    }

    /* Från-år är ändrat */
    public fromYearChanged() {
        this.updateToYears();
        this.updatePeriodFromSelectedYears();
    }
    private updateToYears() {
        let _toYears = [];
        let _min = this.fromYear != undefined ? this.fromYear : this.minYear;
        for (let year = _min; year <= this.maxYear; year++) {
            _toYears.push(year);
        }
        this.toYears = _toYears;
    }

    /* Till-år är ändrat */
    public toYearChanged() {
        this.updateFromYears();
        this.updatePeriodFromSelectedYears();
    }
    private updateFromYears() {
        let _fromYears = [];
        let _max = this.toYear != undefined ? this.toYear : this.maxYear;
        for (let year = this.minYear; year <= _max; year++) {
            _fromYears.push(year);
        }
        this.fromYears = _fromYears;
    }

    /* Beräkna period utifrån valda år i dropdown-listor */
    private updatePeriodFromSelectedYears(): void {
        if (this.fromYear && this.toYear) {
            this.modelFrom = {
                year: Number(this.fromYear),
                month: 1,
                day: 1
            };
            this.modelTo = {
                year: Number(this.toYear),
                month: 12,
                day: 31
            };
            this.periodChange();
        }
    }

    /**
     * Toggle visa senaste året
     */
    public toggleShowCurrentYear(): void {
        if (this.showCurrentYear) {
            this.setCurrentYearPeriod();
            this.dpFrom.navigateTo(this.modelFrom);
            this.dpTo.navigateTo(this.modelTo);
            this.clearAllYears();
            this.periodChange();
        }
    }

    /**
     * Skickar uppdaterad period uppåt och uppdaterar checkboxes.
     */
    public periodChange(): void {
        //Uppdatera Input-parametrarna
        let maxDate = this.maxYear ? moment(`${this.maxYear}-12-31`) : moment();
        this.startdat.data = this.modelFrom != undefined ? this.getDateFromDateStruct(this.modelFrom) : maxDate.subtract({ 'years': 1 }).format("YYYY-MM-DD");
        this.stopdat.data = this.modelTo != undefined ? this.getDateFromDateStruct(this.modelTo) : maxDate.format("YYYY-MM-DD");

        if (this.modelFrom != undefined && this.modelTo != undefined) {
            this.fromMaxDate = { year: this.modelTo.year, month: this.modelTo.month, day: this.modelTo.day };
            this.toMinDate = { year: this.modelFrom.year, month: this.modelFrom.month, day: this.modelFrom.day };
        }

        this.updateYearsFromPeriod();
        //Pusha uppdateringen uppåt
        if (!this.updatingParam) {
            this.periodChanged.emit(new PeriodData(this.startdat, this.stopdat, this.showCurrentYear));
        }
    }

    private getDateFromDateStruct(date: NgbDateStruct) {
        return moment(`${date.year}-${date.month}-${date.day}`, "YYYY-MM-DD").format("YYYY-MM-DD");
    }

}
